import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Briefcase, Plus, MapPin, Calendar, CurrencyDollar, ClockCounterClockwise,
  CheckCircle, XCircle, Handshake, ArrowsDownUp, Trash, ArrowClockwise,
  SortAscending, SortDescending, FloppyDisk, X, Bookmark,
} from "@phosphor-icons/react";
import { loadRates, convertFromUsd, currencySymbol } from "@/lib/fx";

/* v2.91 · Jobs list — role-aware. Client sees their own postings.
   PE sees all open jobs plus any they've been awarded. Admin sees
   everything. */

const STATUS_META = {
  open: { color: "text-emerald-400", bg: "bg-emerald-400/5", border: "border-emerald-400/40", label: "OPEN", icon: Briefcase },
  in_progress: { color: "text-[#00E5FF]", bg: "bg-[#00E5FF]/5", border: "border-[#00E5FF]/40", label: "IN PROGRESS", icon: Handshake },
  completed: { color: "text-white", bg: "bg-white/5", border: "border-white/20", label: "COMPLETED", icon: CheckCircle },
  cancelled: { color: "text-gray-500", bg: "bg-white/5", border: "border-white/10", label: "CANCELLED", icon: XCircle },
};

function fmtUsd(n) {
  if (!n || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v >= 1000) return `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  return `$${v.toFixed(0)}`;
}

export default function JobsPage() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  // v3.2.5 · Three-tab UX for PEs on /jobs — Open · Applied · Pipeline.
  // v3.2.8 · Clients get an extra "Trash" tab for soft-deleted jobs.
  const [bucket, setBucket] = useState("open");
  // v3.2.8 · Sort — always by date for PE (v3.2.10 refactor moved cost/region
  // to filters). Clients still get date/cost/region sort.
  const [sortKey, setSortKey] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  // v3.2.10 · PE-side filters (independent of sort).
  const [filterCountry, setFilterCountry] = useState("");
  const [filterCostRange, setFilterCostRange] = useState("");  // "" | "0-5000" | "5000-25000" | "25000-100000" | "100000-"
  // v3.2.11 · State / Province filter, chained off the country filter.
  const [filterRegion, setFilterRegion] = useState("");
  // Countries seen in the PE's unfiltered fetches — powers the region dropdown.
  const [countryOptions, setCountryOptions] = useState([]);
  // Regions grouped by country ({country: [region, …]}) — populates state/province.
  const [regionsByCountry, setRegionsByCountry] = useState({});
  // v3.2.12 · Saved searches (PE only). Each { id, name, bucket, country, region, cost_range }.
  const [savedSearches, setSavedSearches] = useState([]);
  // v3.2.8 · Prime the FX rate cache once so cards render local currency.
  useEffect(() => { loadRates(); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const role = user?.role;
      const params = {};
      if (role === "pe") {
        params.bucket = bucket;
        // PE: date sort only + explicit filters.
        params.sort  = "date";
        params.order = sortOrder;
        if (filterCountry) params.country = filterCountry;
        if (filterRegion)  params.region  = filterRegion;
        if (filterCostRange) {
          const [lo, hi] = filterCostRange.split("-");
          if (lo)  params.min_cost = Number(lo);
          if (hi)  params.max_cost = Number(hi);
        }
      } else if (role === "client") {
        // v3.2.14 · 4-tab split: open (no proposals) · received (has proposals)
        //                    · awarded (in progress) · trash (soft-deleted).
        params.bucket = bucket;
        params.sort   = sortKey;
        params.order  = sortOrder;
      }
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, [user?.role, bucket, sortKey, sortOrder, filterCountry, filterRegion, filterCostRange]);

  // v3.2 · re-fetch whenever the signed-in user changes (defensive — if
  // JobsPage stays mounted across a fast log-out/log-in swap, we could
  // otherwise render the previous user's jobs for a beat).
  useEffect(() => { load(); }, [load, user?.id]);

  // v3.2.10 · Populate the region-filter dropdown once per bucket.
  // v3.2.12 · Now uses the dedicated /api/jobs/facets aggregation
  // endpoint instead of re-fetching the unfiltered job set.
  useEffect(() => {
    if (user?.role !== "pe") return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/jobs/facets", { params: { bucket } });
        if (!alive) return;
        setCountryOptions(data.countries || []);
        setRegionsByCountry(data.regions_by_country || {});
      } catch { /* silent — dropdown just stays empty */ }
    })();
    return () => { alive = false; };
  }, [user?.role, bucket]);

  // v3.2.12 · Load saved searches once for PE.
  const loadSaved = useCallback(async () => {
    if (user?.role !== "pe") return;
    try {
      const { data } = await api.get("/saved-searches");
      setSavedSearches(data || []);
    } catch { /* silent */ }
  }, [user?.role]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const applySavedSearch = (s) => {
    if (s.bucket && ["open", "applied", "pipeline"].includes(s.bucket)) setBucket(s.bucket);
    setFilterCountry(s.country || "");
    setFilterRegion(s.region || "");
    setFilterCostRange(s.cost_range || "");
    toast.success(`Applied "${s.name}".`);
  };

  const saveCurrentSearch = async () => {
    if (!filterCountry && !filterRegion && !filterCostRange) {
      toast.error("Pick at least one filter first."); return;
    }
    const name = window.prompt("Name this search:", suggestedSearchName());
    if (!name?.trim()) return;
    try {
      const { data } = await api.post("/saved-searches", {
        name: name.trim(),
        bucket,
        country:    filterCountry || null,
        region:     filterRegion  || null,
        cost_range: filterCostRange || null,
      });
      // Upsert into local list (dedupe by id).
      setSavedSearches((xs) => [data, ...xs.filter((x) => x.id !== data.id)]);
      toast.success(`Saved "${data.name}".`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const deleteSaved = async (s) => {
    if (!window.confirm(`Remove saved search "${s.name}"?`)) return;
    try {
      await api.delete(`/saved-searches/${s.id}`);
      setSavedSearches((xs) => xs.filter((x) => x.id !== s.id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const suggestedSearchName = () => {
    const parts = [];
    if (filterCountry) parts.push(filterRegion ? `${filterRegion}, ${filterCountry}` : filterCountry);
    if (filterCostRange) parts.push(`$${filterCostRange.replace("-", "–")}`);
    return parts.join(" · ") || "My search";
  };

  // v3.2.11 · When the country filter changes, drop any stale region
  // selection that doesn't belong to the new country.
  useEffect(() => {
    if (!filterCountry) { setFilterRegion(""); return; }
    const allowed = regionsByCountry[filterCountry] || [];
    if (filterRegion && !allowed.includes(filterRegion)) setFilterRegion("");
  }, [filterCountry, regionsByCountry, filterRegion]);

  const trashJob = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Move this job to trash?")) return;
    try {
      await api.post(`/jobs/${id}/trash`);
      toast.success("Moved to trash.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Trash failed");
    }
  };

  const restoreJob = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/jobs/${id}/restore`);
      toast.success("Restored.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Restore failed");
    }
  };

  const role = user?.role;
  const BUCKET_META_PE = {
    open:      { label: "Open jobs",         empty: "No open jobs match right now. Check back later." },
    applied:   { label: "Applied",            empty: "You haven't applied to any jobs yet — pick one from the Open tab." },
    pipeline:  { label: "In the pipeline",    empty: "No awarded jobs in progress. Winning proposals land here." },
    withdrawn: { label: "Withdrawn",          empty: "No withdrawn proposals. Bids you retract land here in case you change your mind." },
  };
  const BUCKET_META_CLIENT = {
    open:     { label: "Open",     empty: "No open jobs waiting for proposals. Post a job to get started." },
    received: { label: "Received", empty: "No proposals received yet." },
    awarded:  { label: "Awarded",  empty: "No jobs in progress. Accept a proposal to unlock the deliverables folder." },
    trash:    { label: "Trash",    empty: "Trash is empty. Cancelled jobs auto-trash after 21 days." },
  };
  const bucketMeta = role === "pe" ? BUCKET_META_PE : role === "client" ? BUCKET_META_CLIENT : null;
  // Reset the bucket to something valid when the role changes.
  useEffect(() => {
    if (role === "client" && !["open", "received", "awarded", "trash"].includes(bucket)) setBucket("open");
    if (role === "pe" && !["open", "applied", "pipeline", "withdrawn"].includes(bucket)) setBucket("open");
  }, [role, bucket]);

  const currentBucketLabel = role === "pe" ? BUCKET_META_PE[bucket]?.label
                          : role === "client" ? (BUCKET_META_CLIENT[bucket] ? `${BUCKET_META_CLIENT[bucket].label} projects` : "Your projects")
                          : null;
  const title = currentBucketLabel || (role === "client" ? "Your projects" : "All jobs");
  const empty = role === "pe" ? BUCKET_META_PE[bucket]?.empty || ""
             : role === "client" ? (BUCKET_META_CLIENT[bucket]?.empty || "")
             : "No jobs in the system.";

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="label-tag">// jobs</div>
            <h1 className="font-display text-4xl md:text-5xl font-black mt-1">{title}</h1>
            <p className="text-gray-400 mt-2 max-w-2xl">
              {role === "pe" && "RFP-style postings from vetted clients. Submit a proposal to start a conversation."}
              {role === "client" && "Your active + past project postings. Click into any job to review proposals and message engineers."}
              {role === "admin" && "Every job on the platform — for oversight only."}
            </p>
          </div>
          {role === "client" && (
            <Link to="/jobs/new" className="btn-primary inline-flex items-center gap-2" data-testid="jobs-post-btn">
              <Plus size={16} weight="bold" /> Post a job
            </Link>
          )}
        </div>

        <div className="mt-8">
          {(role === "pe" || role === "client") && bucketMeta && (
            <>
              {role === "pe" && savedSearches.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-2"
                     data-testid="jobs-saved-searches">
                  <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider inline-flex items-center gap-1">
                    <Bookmark size={11} weight="fill" className="text-[#FFCC00]" /> saved
                  </span>
                  {savedSearches.map((s) => (
                    <span key={s.id}
                      className="group inline-flex items-center gap-1 border border-[#FFCC00]/40 bg-[#FFCC00]/[0.05] text-[#FFCC00] pl-2 pr-1 py-1 text-[10px] font-mono"
                      data-testid={`jobs-saved-chip-${s.id}`}>
                      <button onClick={() => applySavedSearch(s)}
                        className="hover:underline max-w-[220px] truncate"
                        title={`${s.country || "Any country"} · ${s.region || "Any state"} · ${s.cost_range || "Any cost"}`}>
                        {s.name}
                      </button>
                      <button onClick={() => deleteSaved(s)}
                        className="text-[#FFCC00]/60 hover:text-[#FF3B30] ml-0.5"
                        aria-label="Remove"
                        data-testid={`jobs-saved-delete-${s.id}`}>
                        <X size={10} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex border border-white/10 text-xs font-mono flex-1 min-w-[240px]" data-testid="jobs-bucket-tabs">
                {Object.entries(bucketMeta).map(([k, m]) => {
                  const on = bucket === k;
                  return (
                    <button key={k} onClick={() => setBucket(k)}
                      data-testid={`jobs-tab-${k}`}
                      className={`px-4 py-2 flex-1 transition ${on ? "bg-[#00E5FF] text-black font-bold"
                                                                     : "bg-transparent text-gray-400 hover:text-white"}`}>
                      {m.label}
                    </button>
                  );
                })}
              </div>
              {/* v3.2.10 · PE gets date-sort + region + cost FILTERS.
                   Client keeps date/cost/region sort. */}
              {role === "pe" ? (
                <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono" data-testid="jobs-sort-controls">
                  <ArrowsDownUp size={12} className="text-gray-500" />
                  <span className="text-gray-500 uppercase tracking-wider">by date</span>
                  <button onClick={() => setSortOrder((o) => (o === "desc" ? "asc" : "desc"))}
                    data-testid="jobs-sort-order"
                    className="px-2 py-1 border border-white/10 text-gray-400 hover:text-white inline-flex items-center gap-1">
                    {sortOrder === "desc" ? <SortDescending size={12} /> : <SortAscending size={12} />}
                    {sortOrder}
                  </button>
                  <span className="text-gray-600 mx-1">·</span>
                  <span className="text-gray-500 uppercase tracking-wider inline-flex items-center gap-1">
                    <MapPin size={11} /> region
                  </span>
                  <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}
                    className="bg-black/40 border border-white/10 px-2 py-1 text-[10px] font-mono focus:border-[#00E5FF] outline-none"
                    data-testid="jobs-filter-region">
                    <option value="">All</option>
                    {countryOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {filterCountry && (regionsByCountry[filterCountry] || []).length > 0 && (
                    <>
                      <span className="text-gray-500 uppercase tracking-wider">state/prov</span>
                      <select value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)}
                        className="bg-black/40 border border-white/10 px-2 py-1 text-[10px] font-mono focus:border-[#00E5FF] outline-none"
                        data-testid="jobs-filter-state">
                        <option value="">All</option>
                        {(regionsByCountry[filterCountry] || []).map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </>
                  )}
                  <span className="text-gray-500 uppercase tracking-wider inline-flex items-center gap-1">
                    <CurrencyDollar size={11} /> cost
                  </span>
                  <select value={filterCostRange} onChange={(e) => setFilterCostRange(e.target.value)}
                    className="bg-black/40 border border-white/10 px-2 py-1 text-[10px] font-mono focus:border-[#00E5FF] outline-none"
                    data-testid="jobs-filter-cost">
                    <option value="">Any</option>
                    <option value="0-5000">&lt; $5k</option>
                    <option value="5000-25000">$5k – $25k</option>
                    <option value="25000-100000">$25k – $100k</option>
                    <option value="100000-">&gt; $100k</option>
                  </select>
                  {(filterCountry || filterRegion || filterCostRange) && (
                    <>
                      <button onClick={saveCurrentSearch}
                        className="px-2 py-1 border border-[#FFCC00]/40 text-[#FFCC00] hover:bg-[#FFCC00]/10 inline-flex items-center gap-1"
                        data-testid="jobs-saved-save"
                        title="Save this filter combo">
                        <FloppyDisk size={11} /> save
                      </button>
                      <button onClick={() => { setFilterCountry(""); setFilterRegion(""); setFilterCostRange(""); }}
                        className="px-2 py-1 border border-white/10 text-gray-400 hover:text-white"
                        data-testid="jobs-filter-clear">clear</button>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-[10px] font-mono" data-testid="jobs-sort-controls">
                  <ArrowsDownUp size={12} className="text-gray-500" />
                  <span className="text-gray-500 uppercase tracking-wider">sort</span>
                  {["date", "cost", "region"].map((k) => (
                    <button key={k} onClick={() => setSortKey(k)}
                      data-testid={`jobs-sort-${k}`}
                      className={`px-2 py-1 border ${sortKey === k
                        ? "border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/10"
                        : "border-white/10 text-gray-400 hover:text-white"}`}>
                      {k}
                    </button>
                  ))}
                  <button onClick={() => setSortOrder((o) => (o === "desc" ? "asc" : "desc"))}
                    data-testid="jobs-sort-order"
                    className="px-2 py-1 border border-white/10 text-gray-400 hover:text-white inline-flex items-center gap-1">
                    {sortOrder === "desc" ? <SortDescending size={12} /> : <SortAscending size={12} />}
                    {sortOrder}
                  </button>
                </div>
              )}
            </div>
            </>
          )}
          {loading && <div className="text-sm text-gray-500 font-mono">Loading…</div>}
          {!loading && jobs.length === 0 && (
            <div className="border border-white/10 bg-white/5 p-8 text-center text-sm font-mono text-gray-400"
                 data-testid="jobs-empty">
              <Briefcase size={22} className="mx-auto mb-2 text-gray-500" />
              {empty}
            </div>
          )}
          {!loading && jobs.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {jobs.map((j) => {
                const meta = STATUS_META[j.status] || STATUS_META.open;
                const StatusIcon = meta.icon;
                const cur = j.client_currency || "USD";
                const local = cur !== "USD" ? convertFromUsd(j.budget_estimate_usd, cur) : null;
                return (
                  <Link key={j.id} to={`/jobs/${j.id}`}
                    className={`border border-white/10 bg-[#12151C] p-5 hover:border-[#00E5FF]/60 transition group ${j.trashed_at ? "opacity-60" : ""}`}
                    data-testid={`jobs-card-${j.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display text-lg font-bold group-hover:text-[#00E5FF] transition">{j.title}</div>
                      <div className={`inline-flex items-center gap-1 border ${meta.border} ${meta.bg} px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${meta.color}`}>
                        <StatusIcon size={10} weight="fill" /> {meta.label}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-400 font-mono">
                      {j.client_company || j.client_name}
                    </div>
                    <p className="mt-3 text-xs text-gray-300 line-clamp-2">{j.description}</p>
                    <div className="mt-4 flex flex-wrap gap-3 text-[10px] font-mono text-gray-400">
                      {j.location?.country && (
                        <span className="inline-flex items-center gap-1"><MapPin size={11} /> {j.location.region ? `${j.location.region}, ${j.location.country}` : j.location.country}</span>
                      )}
                      {j.budget_estimate_usd > 0 && (
                        <span className="inline-flex items-center gap-1 text-emerald-400"
                              data-testid={`jobs-budget-${j.id}`}>
                          <CurrencyDollar size={11} />
                          {fmtUsd(j.budget_estimate_usd)}
                          {local != null && (
                            <span className="text-gray-500 ml-1">
                              ≈ {currencySymbol(cur)}{Number(local).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </span>
                          )}
                        </span>
                      )}
                      {j.deadline && (
                        <span className="inline-flex items-center gap-1"><Calendar size={11} /> {j.deadline}</span>
                      )}
                      {role === "client" && j.proposals_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-[#00E5FF]"><ClockCounterClockwise size={11} /> {j.proposals_count} proposal{j.proposals_count === 1 ? "" : "s"}</span>
                      )}
                      {role === "pe" && j.my_proposal && (
                        <span className="inline-flex items-center gap-1 text-[#FFCC00] uppercase tracking-wider"
                              data-testid={`jobs-mystatus-${j.id}`}>
                          <ClockCounterClockwise size={11} /> your proposal: {j.my_proposal.status}
                        </span>
                      )}
                    </div>
                    {(j.specializations_required || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {j.specializations_required.slice(0, 3).map((s) => (
                          <span key={s} className="text-[10px] font-mono border border-[#00E5FF]/30 bg-[#00E5FF]/5 text-[#00E5FF] px-1.5 py-0.5">{s}</span>
                        ))}
                      </div>
                    )}
                    {/* v3.2.8 + v3.2.56 · Trash / restore. Client can
                        delete their OPEN or cancelled/completed jobs
                        (backend blocks awarded/paid). Admin sees the
                        trash affordance on every non-trashed row so
                        moderator cleanup is one click, per new spec. */}
                    {(role === "client" || role === "admin") && (
                      <div className="mt-3 flex justify-end">
                        {j.trashed_at ? (
                          <button onClick={(e) => restoreJob(j.id, e)}
                            className="text-[10px] font-mono text-[#00E5FF] hover:underline inline-flex items-center gap-1"
                            data-testid={`jobs-restore-${j.id}`}>
                            <ArrowClockwise size={11} /> restore
                          </button>
                        ) : (role === "admin" || ["open", "cancelled", "completed"].includes(j.status)) && !j.awarded_pe_id && (
                          <button onClick={(e) => trashJob(j.id, e)}
                            className="text-[10px] font-mono text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-1"
                            data-testid={`jobs-trash-${j.id}`}>
                            <Trash size={11} /> delete
                          </button>
                        )}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
